import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Globe, LayoutDashboard, PanelLeft, Save, Settings, ShieldCheck, User, Camera, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { storage, ref, uploadBytes, getDownloadURL } from './firebase';

function persistSetting(key, value) {
  localStorage.setItem(key, value);
  window.dispatchEvent(new Event('leadszonemap-settings-updated'));
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, userProfile, userRole, updateUserProfile, logout } = useAuth();
  
  // App Settings (Admin only)
  const [apiKey, setApiKey] = useState(localStorage.getItem('leadszonemap_api_key') || '');
  const [lastSavedAt, setLastSavedAt] = useState('');

  // Profile Settings
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ text: '', type: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    persistSetting('leadszonemap_api_key', apiKey);
    setLastSavedAt(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
  }, [apiKey]);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
      setPhotoURL(user.photoURL || '');
    }
    if (userProfile) {
      setWhatsapp(userProfile.whatsapp || '');
    }
  }, [user, userProfile]);

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      // Local preview
      setPhotoURL(URL.createObjectURL(file));
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMessage({ text: '', type: '' });
    
    try {
      let updatedPhotoURL = user?.photoURL || '';
      
      // If a new photo was selected, upload it to Firebase Storage
      if (photoFile) {
        const fileRef = ref(storage, `profile_pictures/${user.uid}/${photoFile.name}`);
        await uploadBytes(fileRef, photoFile);
        updatedPhotoURL = await getDownloadURL(fileRef);
      }
      
      await updateUserProfile(displayName, updatedPhotoURL, email, whatsapp, newPassword || null);
      setPhotoURL(updatedPhotoURL);
      setNewPassword(''); // Clear password field after successful update
      setProfileMessage({ text: 'Perfil atualizado com sucesso!', type: 'success' });
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/requires-recent-login') {
        setProfileMessage({ text: 'Para mudar o e-mail, por favor faça login novamente.', type: 'error' });
      } else {
        setProfileMessage({ text: 'Erro ao atualizar perfil: ' + err.message, type: 'error' });
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="app-modal">
      <div className="modal-backdrop" onClick={() => {}} />
      <div className="modal-card container animate-fade-in">
        <div className="dashboard-shell">
          <aside className="dashboard-sidebar glass">
            <div className="dashboard-navbar">
              <div className="settings-brand">
                <Target size={28} color="var(--accent)" />
                <span>LeadsZone Maps</span>
              </div>

              <div className="dashboard-navbar__links">
                <button className="sidebar-nav__item" onClick={() => navigate('/dashboard')}>
                  <LayoutDashboard size={16} /> Dashboard
                </button>
                <button className="sidebar-nav__item" onClick={() => navigate('/dashboard')}>
                  <Database size={16} /> Base
                </button>
                <button className="sidebar-nav__item active" onClick={() => navigate('/settings')}>
                  <Settings size={16} /> Configurações
                </button>
              </div>

              <button className="sidebar-nav__item" onClick={handleLogout}>
                <Globe size={16} /> Sair
              </button>
            </div>
          </aside>

          <div className="dashboard-content" style={{ overflowY: 'auto' }}>
            <header id="dashboard-overview">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <span style={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Ajustes da Conta</span>
                <h1 style={{ margin: 0 }}>Minha Conta</h1>
                <p className="subtitle" style={{ margin: 0, maxWidth: '68ch' }}>
                  Gerencie seu perfil e suas preferências no LeadsZone Maps.
                </p>
              </div>
            </header>

            <div className="settings-grid" style={{ gridTemplateColumns: '1fr', maxWidth: '800px', margin: '0 auto', gap: '2rem' }}>
              
              {/* ── Profile Section ── */}
              <section className="glass card settings-card settings-card--wide">
                <div className="settings-card__top">
                  <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <User size={18} color="var(--tl-cyan)" /> Meu Perfil
                    </h3>
                    <p>Atualize sua foto, nome de exibição e contatos.</p>
                  </div>
                </div>

                {profileMessage.text && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: profileMessage.type === 'error' ? 'rgba(231,76,60,0.1)' : 'rgba(116, 242, 255, 0.1)',
                    border: `1px solid ${profileMessage.type === 'error' ? 'rgba(231,76,60,0.3)' : 'rgba(116, 242, 255, 0.3)'}`,
                    color: profileMessage.type === 'error' ? '#e74c3c' : 'var(--tl-cyan)'
                  }}>
                    <AlertCircle size={16} /> {profileMessage.text}
                  </div>
                )}

                <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  
                  {/* Photo Avatar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div 
                      onClick={handlePhotoClick}
                      style={{
                        width: '80px', height: '80px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.05)',
                        border: '2px dashed rgba(116, 242, 255, 0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', overflow: 'hidden', position: 'relative'
                      }}
                    >
                      {photoURL ? (
                        <img src={photoURL} alt="Perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <User size={32} color="var(--text-muted)" />
                      )}
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'rgba(0,0,0,0.6)', padding: '4px',
                        display: 'flex', justifyContent: 'center'
                      }}>
                        <Camera size={14} color="#fff" />
                      </div>
                    </div>
                    <div>
                      <h4 style={{ margin: '0 0 0.3rem 0', fontSize: '0.9rem' }}>Foto de Perfil</h4>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>Clique na imagem para enviar uma nova foto.</p>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handlePhotoChange} 
                        accept="image/*" 
                        style={{ display: 'none' }} 
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.2rem' }}>
                    <div>
                      <label className="settings-label" htmlFor="displayName">Nome</label>
                      <input
                        id="displayName"
                        type="text"
                        placeholder="Seu nome"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="glass"
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label className="settings-label" htmlFor="email">E-mail</label>
                      <input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="glass"
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label className="settings-label" htmlFor="whatsapp">WhatsApp</label>
                      <input
                        id="whatsapp"
                        type="tel"
                        placeholder="(11) 99999-9999"
                        value={whatsapp}
                        onChange={(e) => setWhatsapp(e.target.value)}
                        className="glass"
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <label className="settings-label" htmlFor="newPassword">Nova Senha (opcional)</label>
                      <input
                        id="newPassword"
                        type="password"
                        placeholder="Deixe em branco para não alterar"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="glass"
                        style={{ width: '100%' }}
                        minLength={6}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <button type="submit" className="primary" disabled={savingProfile} style={{ minWidth: '140px', justifyContent: 'center' }}>
                      {savingProfile ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
                      {savingProfile ? 'Salvando...' : 'Salvar Perfil'}
                    </button>
                  </div>
                </form>
              </section>

              {/* ── Admin Settings ── */}
              {userRole === 'admin' && (
                <section className="glass card settings-card settings-card--wide">
                  <div className="settings-card__top">
                    <div>
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ShieldCheck size={18} color="var(--accent-hot)" /> Google API Key (Admin)
                      </h3>
                      <p>Fica gravada localmente no navegador e é reaplicada ao dashboard. Apenas Admins veem isso.</p>
                    </div>
                  </div>
                  <div>
                    <label className="settings-label" htmlFor="apiKey">Chave integrada</label>
                    <input
                      id="apiKey"
                      type="password"
                      placeholder="Integrada no Servidor"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="glass"
                      style={{ width: '100%', maxWidth: '400px' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                      As mudanças acima já são refletidas no dashboard. Última atualização: {lastSavedAt || 'agora'}
                    </p>
                  </div>
                </section>
              )}
              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
